import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Tag as TagIcon, Users as UsersIcon, Hammer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

const DEFAULT_COLOR = "#6366f1";
const COLOR_PRESETS = [
  "#6366f1",
  "#22c55e",
  "#f97316",
  "#ef4444",
  "#eab308",
  "#0ea5e9",
  "#a855f7",
  "#64748b",
];

type TagItem = {
  id: string;
  name: string;
  color: string;
  createdAt: string | Date;
  usageCount: number;
};

function TagChip({ name, color }: { name: string; color: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
      style={{
        backgroundColor: `${color}20`,
        borderColor: `${color}55`,
        color,
      }}
    >
      <TagIcon className="h-3 w-3" />
      {name}
    </span>
  );
}

function TagsPanel({
  kind,
  tags,
  isLoading,
  writable,
  onCreate,
  onDelete,
  isMutating,
}: {
  kind: "artisan" | "particulier";
  tags: TagItem[] | undefined;
  isLoading: boolean;
  writable: boolean;
  onCreate: (input: { name: string; color: string }) => void;
  onDelete: (id: string) => void;
  isMutating: boolean;
}) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Saisissez un nom de tag");
      return;
    }
    onCreate({ name: trimmed, color });
    setName("");
    setColor(DEFAULT_COLOR);
  };

  const targetTag = tags?.find((t) => t.id === pendingDeleteId) ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {writable && (
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Nouveau tag {kind === "artisan" ? "artisan" : "particulier"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${kind}-tag-name`}>Nom</Label>
              <Input
                id={`${kind}-tag-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={kind === "artisan" ? "Top performer" : "VIP"}
                maxLength={60}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Couleur</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`h-7 w-7 rounded-full border-2 transition ${
                      color === c ? "border-foreground" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                    aria-label={`Couleur ${c}`}
                  />
                ))}
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-7 w-7 rounded cursor-pointer border"
                  aria-label="Couleur personnalisée"
                />
              </div>
              <div className="pt-1">
                <TagChip name={name.trim() || "Aperçu"} color={color} />
              </div>
            </div>
            <Button
              type="button"
              onClick={submit}
              disabled={!name.trim() || isMutating}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-1" />
              Créer le tag
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className={writable ? "lg:col-span-2" : "lg:col-span-3"}>
        <CardHeader>
          <CardTitle className="text-sm">
            Tags existants ({tags?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Chargement…
            </div>
          ) : !tags || tags.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Aucun tag pour le moment
            </div>
          ) : (
            <ul className="divide-y">
              {tags.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between py-2 gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <TagChip name={t.name} color={t.color} />
                    <Badge variant="outline" className="text-xs">
                      {t.usageCount} utilisé{t.usageCount > 1 ? "s" : ""}
                    </Badge>
                  </div>
                  {writable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPendingDeleteId(t.id)}
                      disabled={isMutating}
                      title={
                        t.usageCount > 0
                          ? "Retirez d'abord les assignations"
                          : "Supprimer"
                      }
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!pendingDeleteId}
        onOpenChange={(o) => !o && setPendingDeleteId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le tag « {targetTag?.name} » ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Cette action est irréversible. {targetTag && targetTag.usageCount > 0
              ? `Ce tag est utilisé par ${targetTag.usageCount} ${
                  kind === "artisan" ? "artisan(s)" : "particulier(s)"
                } — la suppression échouera tant que les assignations existent.`
              : "Aucune assignation, suppression sûre."}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteId(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={isMutating}
              onClick={() => {
                if (pendingDeleteId) {
                  onDelete(pendingDeleteId);
                  setPendingDeleteId(null);
                }
              }}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function OpsTags() {
  const { user } = useAuth();
  const writable = canWrite(user?.staffRole);
  const utils = trpc.useUtils();

  const artisanTagsQuery = trpc.admin.opsTags.listArtisanTags.useQuery();
  const particulierTagsQuery = trpc.admin.opsTags.listParticulierTags.useQuery();

  const createArtisanTag = trpc.admin.opsTags.createArtisanTag.useMutation({
    onSuccess: () => {
      toast.success("Tag artisan créé");
      utils.admin.opsTags.listArtisanTags.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteArtisanTag = trpc.admin.opsTags.deleteArtisanTag.useMutation({
    onSuccess: () => {
      toast.success("Tag supprimé");
      utils.admin.opsTags.listArtisanTags.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const createParticulierTag = trpc.admin.opsTags.createParticulierTag.useMutation({
    onSuccess: () => {
      toast.success("Tag particulier créé");
      utils.admin.opsTags.listParticulierTags.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteParticulierTag = trpc.admin.opsTags.deleteParticulierTag.useMutation({
    onSuccess: () => {
      toast.success("Tag supprimé");
      utils.admin.opsTags.listParticulierTags.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TagIcon className="h-6 w-6" />
            Tags OPS
          </h1>
          <p className="text-sm text-muted-foreground">
            Tags VIP / segmentation pour artisans et particuliers (usage interne staff).
          </p>
        </div>

        <Tabs defaultValue="artisans">
          <TabsList>
            <TabsTrigger value="artisans">
              <Hammer className="h-4 w-4 mr-1" />
              Tags artisans
            </TabsTrigger>
            <TabsTrigger value="particuliers">
              <UsersIcon className="h-4 w-4 mr-1" />
              Tags particuliers
            </TabsTrigger>
          </TabsList>

          <TabsContent value="artisans" className="mt-4">
            <TagsPanel
              kind="artisan"
              tags={artisanTagsQuery.data as TagItem[] | undefined}
              isLoading={artisanTagsQuery.isLoading}
              writable={writable}
              onCreate={(input) => createArtisanTag.mutate(input)}
              onDelete={(id) => deleteArtisanTag.mutate({ id })}
              isMutating={
                createArtisanTag.isPending || deleteArtisanTag.isPending
              }
            />
          </TabsContent>

          <TabsContent value="particuliers" className="mt-4">
            <TagsPanel
              kind="particulier"
              tags={particulierTagsQuery.data as TagItem[] | undefined}
              isLoading={particulierTagsQuery.isLoading}
              writable={writable}
              onCreate={(input) => createParticulierTag.mutate(input)}
              onDelete={(id) => deleteParticulierTag.mutate({ id })}
              isMutating={
                createParticulierTag.isPending || deleteParticulierTag.isPending
              }
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
